import api from './api';
import logger from '../utils/logger';

interface OAuthConfig {
  enabled: boolean;
  tenant_id: string;
  client_id: string;
  authorization_endpoint: string;
  token_endpoint: string;
  redirect_uri: string;
  scopes: string[];
}

/**
 * Get OAuth configuration from backend
 */
export const getOAuthConfig = async (): Promise<OAuthConfig | null> => {
  try {
    const response = await api.get<OAuthConfig>('/auth/oauth-config');
    return response.data;
  } catch {
    return null;
  }
};

/**
 * Check if user should use Entra ID login (has @omnipass.rs UPN)
 */
export const checkIfEntraUser = async (email: string): Promise<{ hasEntraId: boolean; entraUpn?: string }> => {
  try {
    const response = await api.get<{ has_entra_id: boolean; entra_upn?: string }>(
      '/users/check-entra',
      { params: { email } }
    );
    return {
      hasEntraId: response.data.has_entra_id === true,
      entraUpn: response.data.entra_upn,
    };
  } catch {
    return { hasEntraId: false };
  }
};

/**
 * Redirect user to Entra ID login page
 */
export const redirectToEntraLogin = async (email?: string, entraUpn?: string): Promise<void> => {
  const config = await getOAuthConfig();

  if (!config?.enabled) {
    throw new Error('Entra ID authentication is not configured');
  }

  // Obtain a server-signed state token for CSRF protection
  let state: string;
  try {
    const stateResp = await api.get<{ state: string }>('/auth/oauth-init');
    state = stateResp.data.state;
  } catch (err) {
    logger.warn('Server-side CSRF state generation failed; falling back to client-side random state', err);
    state = generateRandomString(32);
  }
  sessionStorage.setItem('oauth_state', state);

  // Generate PKCE verifier and challenge
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  sessionStorage.setItem('pkce_verifier', codeVerifier);

  // Store original email if provided (for validation after callback)
  if (email) {
    sessionStorage.setItem('oauth_email', email);
  }

  // Build authorization URL - use entraUpn for login_hint if available
  const loginHint = entraUpn || email;
  const params = new URLSearchParams({
    client_id: config.client_id,
    response_type: 'code',
    redirect_uri: config.redirect_uri,
    response_mode: 'query',
    scope: config.scopes.join(' '),
    state: state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    ...(loginHint && { login_hint: loginHint }) // Pre-fill with UPN or email
  });

  const authUrl = `${config.authorization_endpoint}?${params.toString()}`;

  globalThis.location.href = authUrl;
};

/**
 * Handle OAuth callback and exchange code for token
 */
export const handleOAuthCallback = async (code: string, state: string): Promise<{ access_token: string }> => {
  // Validate state to prevent CSRF
  const savedState = sessionStorage.getItem('oauth_state');
  if (!savedState || savedState !== state) {
    throw new Error('Invalid state parameter - possible CSRF attack');
  }

  // Retrieve PKCE verifier generated at login initiation
  const codeVerifier = sessionStorage.getItem('pkce_verifier');

  const config = await getOAuthConfig();
  if (!config) {
    throw new Error('OAuth configuration not available');
  }

  // Exchange code for token via backend
  const response = await api.post<{ access_token: string }>(
    '/auth/oauth/callback',
    {
      code,
      redirect_uri: config.redirect_uri,
      state,
      ...(codeVerifier && { code_verifier: codeVerifier }),
    }
  );

  // Clean up session storage
  sessionStorage.removeItem('oauth_state');
  sessionStorage.removeItem('oauth_email');
  sessionStorage.removeItem('pkce_verifier');

  return { access_token: response.data.access_token };
};

/**
 * Generate random string for state parameter
 */
function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  for (let i = 0; i < length; i++) {
    result += chars[randomValues[i] % chars.length];
  }

  return result;
}

/**
 * Generate a PKCE code verifier (RFC 7636)
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCodePoint(...Array.from(array)))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '');
}

/**
 * Derive a PKCE S256 code challenge from a verifier
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCodePoint(...Array.from(new Uint8Array(digest))))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '');
}
